/**
 * @ken/module-taxes — frontend entry point.
 *
 * Story-page migration (ADR-0081/0082): the in-list detail Sheets are RETIRED
 * for both sub-entities. The taxes module now mounts:
 *   - /taxes and /taxes/codes and /taxes/discounts — the TaxesListPage (two
 *     sub-views via ?view= query param)
 *   - taxes/$id — full-page TaxCode story detail (CONFIG, NO hero)
 *   - taxes/discounts/$id — full-page DiscountRule story detail (CONFIG, NO hero)
 *
 * The list validates search params (q, filter_status, highlight) so that
 * back-navigation from story pages restores filter state and refocuses the
 * originating row.
 */

import {
  lazyScreen,
  type AnyRoute,
  type CreateModuleRoutes,
} from "@ken/cockpit-types";
import { isRecordId, RecordNotFound } from "@ken/ui";
import { createRoute, notFound, redirect } from "@tanstack/react-router";

// One loader per screen file, shared by every screen that file exports, so the
// route table carries each file's import (and its preload list) once (SF-BUNDLE).
const loadTaxCodeStoryTabs = () => import("./routes/story/tax-code-story-tabs");
const loadDiscountRuleStoryTabs = () => import("./routes/story/discount-rule-story-tabs");

// Screens download the first time they are opened (ADR-040, BND-01). The
// route table below stays eager: paths, params, validateSearch, redirects.
const TaxesListPage = lazyScreen(
  () => import("./routes/taxes-list"),
  "TaxesListPage",
);
const TaxCodeCreatePage = lazyScreen(
  () => import("./routes/tax-code-create"),
  "TaxCodeCreatePage",
);
const DiscountRuleCreatePage = lazyScreen(
  () => import("./routes/discount-rule-create"),
  "DiscountRuleCreatePage",
);
const TaxCodeStoryDetail = lazyScreen(
  () => import("./routes/story/tax-code-story-detail"),
  "TaxCodeStoryDetail",
);
const TaxCodeActivityRoute = lazyScreen(
  loadTaxCodeStoryTabs,
  "TaxCodeActivityRoute",
);
const TaxCodeOverviewRoute = lazyScreen(
  loadTaxCodeStoryTabs,
  "TaxCodeOverviewRoute",
);
const DiscountRuleStoryDetail = lazyScreen(
  () => import("./routes/story/discount-rule-story-detail"),
  "DiscountRuleStoryDetail",
);
const DiscountRuleActivityRoute = lazyScreen(
  loadDiscountRuleStoryTabs,
  "DiscountRuleActivityRoute",
);
const DiscountRuleOverviewRoute = lazyScreen(
  loadDiscountRuleStoryTabs,
  "DiscountRuleOverviewRoute",
);

// UI-06: taxes carries two id routes, and `taxes/discounts/$id` was swallowing
// the literal `new` that the list's own Add discount button links to — a
// button that reached a detail page for a discount rule whose id was the word
// "new", and rendered nothing.
//
// Taxes IS the one module of the six where a create surface is in scope: UI-07
// builds the tax-code and discount-rule forms and replaces what these static
// routes render. UI-06's job is only that the id routes stop swallowing the
// segment, so that work has somewhere to land and nothing renders blank
// meanwhile.

export const createRoutes: CreateModuleRoutes = (parentRoute: AnyRoute) => {
  // ── List routes (validateSearch for filter/search/highlight restore) ───────
  const listRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "taxes",
    validateSearch: (raw: Record<string, unknown>): TaxesListSearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["highlight"] === "string" && raw["highlight"]
        ? { highlight: raw["highlight"] }
        : {}),
      ...(typeof raw["view"] === "string" && raw["view"]
        ? { view: raw["view"] }
        : {}),
      // POP-01 (ADR-0229): the record open in the pop-up, so a reload
      // reopens it and the browser's back button closes it.
      ...(typeof raw["open"] === "string" && raw["open"]
        ? { open: raw["open"] }
        : {}),
      // POP-02 (ADR-0230): the pop-up's open tab.
      ...(typeof raw["tab"] === "string" && raw["tab"]
        ? { tab: raw["tab"] }
        : {}),
    }),
    component: TaxesListPage,
  });

  const codesRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "taxes/codes",
    validateSearch: (raw: Record<string, unknown>): TaxesListSearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["highlight"] === "string" && raw["highlight"]
        ? { highlight: raw["highlight"] }
        : {}),
      ...(typeof raw["view"] === "string" && raw["view"]
        ? { view: raw["view"] }
        : {}),
      // POP-01 (ADR-0229): the record open in the pop-up, so a reload
      // reopens it and the browser's back button closes it.
      ...(typeof raw["open"] === "string" && raw["open"]
        ? { open: raw["open"] }
        : {}),
      // POP-02 (ADR-0230): the pop-up's open tab.
      ...(typeof raw["tab"] === "string" && raw["tab"]
        ? { tab: raw["tab"] }
        : {}),
    }),
    component: TaxesListPage,
  });

  const discountsRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "taxes/discounts",
    validateSearch: (raw: Record<string, unknown>): TaxesListSearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["highlight"] === "string" && raw["highlight"]
        ? { highlight: raw["highlight"] }
        : {}),
      ...(typeof raw["view"] === "string" && raw["view"]
        ? { view: raw["view"] }
        : {}),
      // POP-01 (ADR-0229): the record open in the pop-up, so a reload
      // reopens it and the browser's back button closes it.
      ...(typeof raw["open"] === "string" && raw["open"]
        ? { open: raw["open"] }
        : {}),
      // POP-02 (ADR-0230): the pop-up's open tab.
      ...(typeof raw["tab"] === "string" && raw["tab"]
        ? { tab: raw["tab"] }
        : {}),
    }),
    component: TaxesListPage,
  });

  // UI-06 registered STATIC routes for the literal `new` at both depths, so
  // neither id route can match it. `taxes/codes/new` is where the list's Add
  // tax code button points; `taxes/discounts/new` is where Add discount points
  // and is the one `taxes/discounts/$id` used to swallow. UI-07 replaces what
  // they render: both now carry the real create form, which is what the two
  // buttons were always offering.
  const literalNewCodeRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "taxes/codes/new",
    component: TaxCodeCreatePage,
  });
  const literalNewDiscountRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "taxes/discounts/new",
    component: DiscountRuleCreatePage,
  });

  // ── TaxCode story-page: taxes/$id (CONFIG archetype — NO hero) ────────────
  const taxCodeDetailRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "taxes/$id",
    // UI-06's id-shape guard: a segment that could never be a record id stops
    // here, BEFORE the detail read is issued.
    beforeLoad: ({ params }) => {
      if (!isRecordId((params as { id?: string }).id)) throw notFound();
    },
    notFoundComponent: RecordNotFound,
    // Carry list filter/search into the detail route so Prev/Next walks the
    // SAME filtered set and back restores state.
    validateSearch: (raw: Record<string, unknown>): TaxCodeStorySearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
    }),
    component: TaxCodeStoryDetail,
  });

  // Index redirect taxes/$id → …/overview
  const taxCodeDetailIndexRoute = createRoute({
    getParentRoute: () => taxCodeDetailRoute,
    path: "/",
    beforeLoad: ({ params }) => {
      throw redirect({
        to: "/$tenantSlug/taxes/$id/overview",
        params: params as never,
        replace: true,
      });
    },
  });

  const taxCodeOverviewRoute = createRoute({
    getParentRoute: () => taxCodeDetailRoute,
    path: "overview",
    component: TaxCodeOverviewRoute,
  });
  const taxCodeActivityRoute = createRoute({
    getParentRoute: () => taxCodeDetailRoute,
    path: "activity",
    component: TaxCodeActivityRoute,
  });

  (
    taxCodeDetailRoute as unknown as { addChildren: (c: unknown) => void }
  ).addChildren([
    taxCodeDetailIndexRoute,
    taxCodeOverviewRoute,
    taxCodeActivityRoute,
  ]);

  // ── DiscountRule story-page: taxes/discounts/$id (CONFIG — NO hero) ───────
  const discountRuleDetailRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "taxes/discounts/$id",
    // UI-06's id-shape guard — the route that was swallowing the Add discount
    // button's target.
    beforeLoad: ({ params }) => {
      if (!isRecordId((params as { id?: string }).id)) throw notFound();
    },
    notFoundComponent: RecordNotFound,
    validateSearch: (
      raw: Record<string, unknown>,
    ): DiscountRuleStorySearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
    }),
    component: DiscountRuleStoryDetail,
  });

  // Index redirect taxes/discounts/$id → …/overview
  const discountRuleDetailIndexRoute = createRoute({
    getParentRoute: () => discountRuleDetailRoute,
    path: "/",
    beforeLoad: ({ params }) => {
      throw redirect({
        to: "/$tenantSlug/taxes/discounts/$id/overview",
        params: params as never,
        replace: true,
      });
    },
  });

  const discountRuleOverviewRoute = createRoute({
    getParentRoute: () => discountRuleDetailRoute,
    path: "overview",
    component: DiscountRuleOverviewRoute,
  });
  const discountRuleActivityRoute = createRoute({
    getParentRoute: () => discountRuleDetailRoute,
    path: "activity",
    component: DiscountRuleActivityRoute,
  });

  (
    discountRuleDetailRoute as unknown as { addChildren: (c: unknown) => void }
  ).addChildren([
    discountRuleDetailIndexRoute,
    discountRuleOverviewRoute,
    discountRuleActivityRoute,
  ]);

  // Static before parameterised.
  return [
    listRoute,
    codesRoute,
    discountsRoute,
    literalNewCodeRoute,
    literalNewDiscountRoute,
    taxCodeDetailRoute,
    discountRuleDetailRoute,
  ];
};

/** List search params (filter/search/highlight/view) — URL-borne for restore. */
export interface TaxesListSearch {
  q?: string;
  filter_status?: string;
  highlight?: string;
  view?: string;
  /** POP-01: the record open in the pop-up over the list. */
  open?: string;
  /** POP-02: the pop-up's open tab (absent: Overview). */
  tab?: string;
}

/** TaxCode story-detail search params (carry the list's filter/search). */
export interface TaxCodeStorySearch {
  q?: string;
  filter_status?: string;
}

/** DiscountRule story-detail search params (carry the list's filter/search). */
export interface DiscountRuleStorySearch {
  q?: string;
  filter_status?: string;
}
